/**
 * Security utilities for token and sensitive data handling
 * 
 * Note: For production use with httpOnly cookies, you need server-side support.
 * This module provides client-side security enhancements where possible.
 */

// Token storage key
const TOKEN_KEY = 'token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const SESSION_KEY = 'session_id';

// Session duration (24 hours in milliseconds)
const SESSION_DURATION = 24 * 60 * 60 * 1000;

/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return false; // No expiry claim
    
    // exp is in seconds, Date.now() is in milliseconds
    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // If we can't parse it, consider it expired
  }
}

/**
 * Get token payload without verification (client-side only)
 */
export function getTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

/**
 * Securely store token with expiry tracking
 */
export function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    
    // Track expiry time
    const payload = getTokenPayload(token);
    if (payload?.exp && typeof payload.exp === 'number') {
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(payload.exp * 1000));
    } else {
      // Default to session duration if no exp claim
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + SESSION_DURATION));
    }
    
    // Generate session ID for tracking
    if (!sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, generateSessionId());
    }
  } catch (error) {
    console.error('Failed to store token:', error);
  }
}

/**
 * Get stored token if valid
 */
export function getToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    // Check expiry
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiry && parseInt(expiry) < Date.now()) {
      clearToken();
      return null;
    }
    
    // Check if token is valid JWT
    if (token.split('.').length === 3 && isTokenExpired(token)) {
      clearToken();
      return null;
    }
    
    return token;
  } catch {
    return null;
  }
}

/**
 * Clear all authentication data
 */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch (error) {
    console.error('Failed to clear token:', error);
  }
}

/**
 * Generate a random session ID
 */
function generateSessionId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Check if current session is valid
 */
export function isSessionValid(): boolean {
  const token = getToken();
  const sessionId = sessionStorage.getItem(SESSION_KEY);
  return token !== null && sessionId !== null;
}

/**
 * Sanitize user input to prevent XSS
 */
export function sanitizeInput(input: string): string {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Rate limiting helper - tracks attempts per key
 */
const rateLimitMap = new Map<string, { count: number; resetTime: number }>();

export function checkRateLimit(key: string, maxAttempts: number, windowMs: number): boolean {
  const now = Date.now();
  const record = rateLimitMap.get(key);
  
  if (!record || record.resetTime < now) {
    rateLimitMap.set(key, { count: 1, resetTime: now + windowMs });
    return true;
  }
  
  if (record.count >= maxAttempts) {
    return false;
  }
  
  record.count++;
  return true;
}

/**
 * Get time until rate limit resets
 */
export function getRateLimitResetTime(key: string): number {
  const record = rateLimitMap.get(key);
  if (!record) return 0;
  return Math.max(0, record.resetTime - Date.now());
}
